import {NewsItemService} from "../../service/items/NewsItemService";
import {ReadNewsItemsStore} from "../read-items/ReadNewsItemsStore";
import {NewsHeadlineStreamStore} from "../stream/NewsHeadlineStreamStore";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {CodeAndExchange, Securities, securityEquality} from "../../../instruments/types/CodeAndExchange";
import {OffsetDateTime} from "../../../pseudo-types/DateTypes";
import {NewsHeadlineRecord, NewsHeadlineRecords} from "../../models/NewsHeadlineRecord";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import moment from "moment";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {Optional} from "../../../js/util/Optional";
import {addDateSeparators, BuildModels, NewsHeadlineDataRows} from "../../models/NewsHeadlineViewModel";
import {NewsItemId} from "../../types/NewsItemId";
import {SelectedNewsSources} from "../../components/news-source-selector/model/SelectedNewsSources";
import {buildSourceFilter} from "./NewsStoreUtil";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {NewsSessionIdStore} from "../news-id/NewsSessionIdStore";

const TargetNewsItemsCount = 20;

export class NewsStore {

    private readonly codes$ = new ReplaySubject<Securities | null>(1);
    private readonly newsSources$ = new BehaviorSubject<SelectedNewsSources | null>(null);
    private readonly lastDate$ = new BehaviorSubject<OffsetDateTime | null>(null);
    private readonly retrieveMore$ = new BehaviorSubject(true);
    private readonly headlines$: Observable<NewsHeadlineDataRows>;
    private readonly noMoreHistory$ = new BehaviorSubject(false);

    public constructor(private readonly service: NewsItemService,
                       private readonly readItemStore: ReadNewsItemsStore,
                       private readonly headlineStore: NewsHeadlineStreamStore,
                       private readonly newsSessionIdStore: NewsSessionIdStore) {
        this.headlines$ = this.buildModels();
    }

    private buildModels(): Observable<NewsHeadlineDataRows> {
        const headlines$ = this.headlinesSource();
        const sessionId$ = this.newsSessionIdStore.current.distinctUntilChanged();
        const modelSource$ = Observable.combineLatest(headlines$, this.readItemStore.readItems, sessionId$);
        const models$ = modelSource$.map(([headlines, readItems, sessionId]) => BuildModels(headlines,
                                                                                            readItems,
                                                                                            sessionId));
        const filter$ = this.newsSources$.map(buildSourceFilter);

        const source$ = Observable.combineLatest(models$, filter$);

        return source$.map(([models, filter]) => models.filter(filter))
                      .map(addDateSeparators)
                      .publishReplay(1)
                      .refCount();
    }

    private headlinesSource(): Observable<NewsHeadlineRecords> {
        return this.codes$
                   .distinctUntilChanged((a, b) => {
                       if (a == null || b == null) {
                           return a === b;
                       } else {
                           return ArrayUtil.matches(a, b, securityEquality());
                       }
                   })
                   .switchMap((codes) => {
                       // Done manually here to avoid any internal subscriptions
                       this.lastDate$.next(null);

                       const history$ = this.newsHistorySource(codes);
                       const updates$ = this.headlineUpdates(codes);
                       const result = Observable.merge(history$, updates$)
                                                .scan((acc: NewsHeadlineRecords, next) => acc.concat(next), []);
                       // The typing does not like having union types for the "next" value that are half of the type
                       // that will be the result of the entire scan will be
                       return result as Observable<NewsHeadlineRecords>;
                   });
    }

    private newsHistorySource(codes: Securities | null): Observable<NewsHeadlineRecords> {
        return this.retrieveMore$
                   .withLatestFrom(this.lastDate$)
                   .switchMap(([_, last]) => this.retrieveHeadlines(codes, last))
                   .do((headlines) => this.updateLastDate(headlines))
                   .scan((acc, next) => acc.concat(next), [])
                   .startWith([]);
    }

    private retrieveHeadlines(codes: Securities | null, last: OffsetDateTime | null): Observable<NewsHeadlineRecords> {
        const historyStart = Optional.of(last)
                                     .map(moment)
                                     .orElseGet(() => moment());
        const historyStartTime = DateTimeUtil.offsetDateTime(historyStart);
        return Observable.fromPromise(this.service.headlineHistory(codes, historyStartTime, TargetNewsItemsCount))
                         .do((history) => {
                             if (history.length === 0) {
                                 this.noMoreHistory$.next(true);
                             }
                         })
                         .catch(() => Observable.empty());
    }

    private updateLastDate(headlines: NewsHeadlineRecords) {
        if (headlines.length === 0) {
            return;
        }
        const last = headlines.map((h) => h.at).sort()[0];
        this.lastDate$.next(last);
    }

    private headlineUpdates(codes: Securities | null): Observable<NewsHeadlineRecord> {
        if (codes == null) {
            return this.headlineStore.headlines();
        } else {
            return this.headlineStore.headlines(codes);
        }
    }

    public updateCode(codeOrCodes: CodeAndExchange | Securities | null) {
        if (codeOrCodes == null) {
            this.codes$.next(null);
        } else if (ArrayUtil.isArray(codeOrCodes)) {
            const securities = codeOrCodes.map((c) => ObjectUtil.pick(c, "code", "exchange"));
            this.codes$.next(securities);
        } else {
            const security = ObjectUtil.pick(codeOrCodes, "code", "exchange");
            this.codes$.next([security]);
        }
    }

    public updateSources(sources: SelectedNewsSources | null) {
        this.newsSources$.next(sources);
    }

    public markRead(id: NewsItemId) {
        this.readItemStore.markRead(id);
    }

    public get headlines(): Observable<NewsHeadlineDataRows> {
        return this.headlines$;
    }

    public loadMoreHistory() {
        this.retrieveMore$.next(true);
    }

    public noMoreHistory(): Observable<boolean> {
        return this.noMoreHistory$;
    }
}
